import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useSession } from '@/features/auth/store';
import { useI18n } from '@/lib/i18n';
import {
  DatePrecision,
  DateQualifier,
  FamilyRole,
  hasAtLeastRole,
} from '@heritage/shared';
import { formatPartialDate } from '@/lib/partialDate';
import { formatPersonName, useNameFormat } from '@/lib/names';
import { useDebouncedValue } from '@/lib/useDebouncedValue';
import { dataApi } from '@/lib/api';
import { cachedGet } from '@/lib/cache';
import { PersonAutocomplete } from './PersonAutocomplete';
import { requestPersistentStorage } from '@/lib/db';
import { startSyncLoop } from '@/lib/sync';
import { Icon } from './Icon';
import { LanguageSwitcher } from './LanguageSwitcher';
import { ThemeSwitcher } from './ThemeSwitcher';
import { useTheme } from '@/lib/theme';
import { OfflineBanner } from './OfflineBanner';
import { AppUpdateBanner } from './AppUpdateBanner';
import { PushEnablePrompt } from './PushEnablePrompt';
import { AssistantPanel } from './AssistantPanel';
import {
  clearPendingSharedFiles,
  getPendingSharedFiles,
  nativeShare,
  readSharedFile,
  type SharedFileInfo,
} from '@/lib/native-share';

interface NavItem {
  label: string;
  to: string;
  icon: 'home' | 'tree' | 'chat' | 'book' | 'calendar' | 'map';
  end?: boolean;
  badge?: number;
}

export function AppShell() {
  const { t, locale } = useI18n();
  const nameFormat = useNameFormat();
  const [search, setSearch] = useState('');
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [assistantInitialFiles, setAssistantInitialFiles] = useState<File[] | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchBoxRef = useRef<HTMLDivElement | null>(null);
  const debouncedSearch = useDebouncedValue(search, 250).trim();
  const memberships = useSession((state) => state.memberships);
  const activeFamilyId = useSession((state) => state.activeFamilyId);
  const setActiveFamily = useSession((state) => state.setActiveFamily);
  const activeSpaceId = useSession((state) => state.activeSpaceId);
  const setActiveSpace = useSession((state) => state.setActiveSpace);
  const currentUserId = useSession((state) => state.user?.id);
  const serverTheme = useSession((state) => state.user?.settings?.theme);
  const setTheme = useTheme((state) => state.setTheme);
  const appliedThemeUserRef = useRef<string | null>(null);
  const activePersonId = useSession((state) => state.activePersonId);
  const setActivePerson = useSession((state) => state.setActivePerson);
  const navigate = useNavigate();

  useEffect(() => {
    void requestPersistentStorage();
    return startSyncLoop(() => useSession.getState().activeFamilyId);
  }, []);

  const family = memberships.find((m) => m.familyId === activeFamilyId);
  const canUseAssistant = Boolean(
    family &&
      family.status === 'ACTIVE' &&
      hasAtLeastRole(family.role as FamilyRole, FamilyRole.MEMBER),
  );

  useEffect(() => {
    if (!activeFamilyId || !canUseAssistant) return;
    let disposed = false;
    let handle: { remove(): Promise<void> } | null = null;
    const seen = new Set<string>();
    const consume = async (items: SharedFileInfo[]) => {
      if (disposed) return;
      const files: File[] = [];
      for (const item of items) {
        if (seen.has(item.path)) continue;
        seen.add(item.path);
        try {
          files.push(await readSharedFile(item));
        } catch {
          // Skip unreadable shared files.
        }
      }
      if (files.length === 0) return;
      setAssistantInitialFiles((current) => [
        ...(current ?? []),
        ...files,
      ]);
      setAssistantOpen(true);
      await clearPendingSharedFiles();
    };
    void getPendingSharedFiles().then(consume);
    void nativeShare
      .addListener('sharedFiles', (data) => {
        void consume(data.files ?? []);
      })
      .then((listener) => {
        if (disposed) void listener.remove();
        else handle = listener;
      })
      .catch(() => undefined);
    return () => {
      disposed = true;
      if (handle) void handle.remove();
    };
  }, [activeFamilyId, canUseAssistant]);

  const spaces = useQuery({
    queryKey: ['spaces', activeFamilyId],
    queryFn: () => dataApi.spaces(activeFamilyId!),
    enabled: Boolean(activeFamilyId),
  });

  useEffect(() => {
    if (!currentUserId || appliedThemeUserRef.current === currentUserId) return;
    appliedThemeUserRef.current = currentUserId;
    if (
      serverTheme === 'light' ||
      serverTheme === 'dark' ||
      serverTheme === 'system'
    ) {
      setTheme(serverTheme);
    }
  }, [currentUserId, serverTheme, setTheme]);

  useEffect(() => {
    if (!activeSpaceId || !spaces.data) return;
    if (!spaces.data.some((space) => space.id === activeSpaceId)) {
      setActiveSpace(null);
    }
  }, [activeSpaceId, setActiveSpace, spaces.data]);

  const tree = useQuery({
    queryKey: ['tree', activeFamilyId],
    queryFn: () =>
      cachedGet(
        `tree:${activeFamilyId}`,
        () => dataApi.tree(activeFamilyId!),
        activeFamilyId,
      ),
    enabled: Boolean(activeFamilyId),
  });

  const chatUnread = useQuery({
    queryKey: ['chats-unread', activeFamilyId],
    queryFn: () => dataApi.chatUnread(activeFamilyId!),
    enabled: Boolean(activeFamilyId),
    refetchInterval: 30_000,
    staleTime: 10_000,
  });
  const unreadChatCount = chatUnread.data?.unread ?? 0;

  useEffect(() => {
    if (!activePersonId || !tree.data) return;
    if (!tree.data.persons.some((person) => person.id === activePersonId)) {
      setActivePerson(null);
    }
  }, [activePersonId, setActivePerson, tree.data]);

  const suggestions = useQuery({
    queryKey: ['search-suggest', activeFamilyId, debouncedSearch],
    queryFn: () => dataApi.search(activeFamilyId!, debouncedSearch),
    enabled:
      Boolean(activeFamilyId) &&
      searchOpen &&
      debouncedSearch.length >= 1,
    staleTime: 15_000,
  });

  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (
        searchBoxRef.current &&
        !searchBoxRef.current.contains(event.target as Node)
      ) {
        setSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);


  const items: NavItem[] = activeFamilyId
    ? [
        { label: 'Главная', to: '/', icon: 'home', end: true },
        {
          label: 'Древо',
          to: `/families/${activeFamilyId}/tree`,
          icon: 'tree',
        },
        {
          label: 'Чаты',
          to: `/families/${activeFamilyId}/chats`,
          icon: 'chat',
          badge: unreadChatCount,
        },
        {
          label: 'Дневник',
          to: `/families/${activeFamilyId}/diary`,
          icon: 'book',
        },
        {
          label: 'Карта',
          to: `/families/${activeFamilyId}/map`,
          icon: 'map',
        },
      ]
    : [{ label: 'Главная', to: '/', icon: 'home', end: true }];

  return (
    <div className="min-h-[100dvh] bg-heritage-50">
      <OfflineBanner />
      <header className="sticky top-0 z-[1000] border-b border-heritage-100 bg-heritage-50/95 backdrop-blur">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-3 px-4 py-3">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="flex items-center gap-2"
          >
            <img
              src="/icons/icon-192-v2.png"
              alt=""
              aria-hidden="true"
              className="h-9 w-9 shadow-sm"
            />
            <span className="hidden text-lg font-semibold tracking-tight sm:block">
              {t('Семья P&Co')}
            </span>
          </button>

          {memberships.length > 1 && (
            <select
              value={activeFamilyId ?? ''}
              onChange={(event) => {
                setActiveFamily(event.target.value);
                navigate('/');
              }}
              className="min-w-0 max-w-[150px] rounded-xl border border-heritage-200 bg-surface px-2 py-2 text-sm outline-none focus:border-heritage-500"
              aria-label={t('Семья')}
            >
              {memberships.map((membership) => (
                <option key={membership.familyId} value={membership.familyId}>
                  {membership.family.name}
                </option>
              ))}
            </select>
          )}

          {activeFamilyId && (spaces.data?.length ?? 0) > 0 && (
            <select
              value={activeSpaceId ?? ''}
              onChange={(event) => setActiveSpace(event.target.value || null)}
              className="min-w-0 max-w-[170px] flex-1 rounded-xl border border-heritage-200 bg-surface px-2 py-2 text-sm outline-none focus:border-heritage-500 sm:max-w-[220px]"
              aria-label={t('Активное пространство')}
            >
              <option value="">{t('Все пространства')}</option>
              {(spaces.data ?? []).map((space) => (
                <option key={space.id} value={space.id}>
                  {space.name}
                </option>
              ))}
            </select>
          )}

          {activeFamilyId && (
            <div className="min-w-[150px] max-w-[190px] flex-1">
              <PersonAutocomplete
                persons={tree.data?.persons ?? []}
                value={activePersonId}
                onChange={setActivePerson}
                label={t('Персона')}
                placeholder={t('Персона')}
                hideLabel
              />
            </div>
          )}

          {activeFamilyId && (
            <div
              ref={searchBoxRef}
              className="relative hidden min-w-0 flex-1 md:block md:max-w-md"
            >
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  const query = search.trim();
                  if (query.length >= 1) {
                    setSearchOpen(false);
                    navigate(
                      `/families/${activeFamilyId}/search?q=${encodeURIComponent(query)}`,
                    );
                  }
                }}
              >
                <div className="relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-heritage-400">
                    <Icon name="search" className="h-4 w-4" />
                  </span>
                  <input
                    value={search}
                    onChange={(event) => {
                      setSearch(event.target.value);
                      setSearchOpen(true);
                    }}
                    onFocus={() => setSearchOpen(true)}
                    className="input py-2 pl-9"
                    placeholder={t('Поиск по семье…')}
                    aria-label={t('Поиск по семье')}
                  />
                </div>
              </form>

              {searchOpen && debouncedSearch.length >= 1 && (
                <div className="absolute left-0 right-0 top-full z-50 mt-2 max-h-[70vh] overflow-y-auto rounded-xl border border-heritage-100 bg-surface p-2 shadow-xl">
                  {suggestions.isLoading && (
                    <p className="px-2 py-1.5 text-xs text-heritage-500">
                      {t('Ищем…')}
                    </p>
                  )}
                  {!suggestions.isLoading &&
                    (suggestions.data?.persons.length ?? 0) === 0 &&
                    (suggestions.data?.facts.length ?? 0) === 0 && (
                      <p className="px-2 py-1.5 text-xs text-heritage-500">
                        {t('Ничего не найдено')}
                      </p>
                    )}

                  {(suggestions.data?.persons.length ?? 0) > 0 && (
                    <div>
                      <p className="px-2 pt-1 text-[10px] uppercase tracking-wide text-heritage-400">
                        {t('Люди')}
                      </p>
                      {suggestions.data!.persons.slice(0, 3).map((person) => (
                        <button
                          key={person.id}
                          type="button"
                          onClick={() => {
                            setSearchOpen(false);
                            navigate(
                              `/families/${activeFamilyId}/persons/${person.id}`,
                            );
                          }}
                          className="block w-full truncate rounded-lg px-2 py-1.5 text-left text-sm hover:bg-heritage-50"
                        >
                          {formatPersonName(person, nameFormat)}
                          {(person.birthYear || person.birthDateRaw) && (
                            <span className="ml-2 text-xs text-heritage-400">
                              {formatPartialDate(
                                {
                                  year: person.birthYear,
                                  month: person.birthMonth,
                                  day: person.birthDay,
                                  precision:
                                    (person.birthPrecision as DatePrecision) ??
                                    DatePrecision.UNKNOWN,
                                  qualifier:
                                    (person.birthQualifier as DateQualifier) ??
                                    DateQualifier.EXACT,
                                  raw: person.birthDateRaw,
                                },
                                locale,
                              )}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}

                  {(suggestions.data?.facts.length ?? 0) > 0 && (
                    <div>
                      <p className="px-2 pt-1 text-[10px] uppercase tracking-wide text-heritage-400">
                        {t('Дневник')}
                      </p>
                      {suggestions.data!.facts.slice(0, 3).map((fact) => (
                        <button
                          key={fact.id}
                          type="button"
                          onClick={() => {
                            setSearchOpen(false);
                            navigate(
                              `/families/${activeFamilyId}/diary?record=${fact.id}`,
                            );
                          }}
                          className="block w-full truncate rounded-lg px-2 py-1.5 text-left text-sm hover:bg-heritage-50"
                        >
                          {formatPersonName(fact.person, nameFormat)}
                          <span className="ml-2 text-xs text-heritage-400">
                            {[
                              fact.value,
                              formatPartialDate(
                                {
                                  year: fact.dateYear,
                                  month: fact.dateMonth,
                                  day: fact.dateDay,
                                  precision:
                                    (fact.datePrecision as DatePrecision) ??
                                    DatePrecision.UNKNOWN,
                                  qualifier:
                                    (fact.dateQualifier as DateQualifier) ??
                                    DateQualifier.EXACT,
                                  raw: fact.dateRaw,
                                },
                                locale,
                              ),
                              fact.place,
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => {
                      setSearchOpen(false);
                      navigate(
                        `/families/${activeFamilyId}/search?q=${encodeURIComponent(debouncedSearch)}`,
                      );
                    }}
                    className="mt-1 block w-full rounded-lg bg-heritage-50 px-2 py-2 text-left text-xs font-medium text-heritage-700 hover:bg-heritage-100"
                  >
                    {t('Все результаты')}
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="ml-auto flex items-center gap-1">
            <ThemeSwitcher />
            <LanguageSwitcher />
            {activeFamilyId && (
              <button
                type="button"
                onClick={() => navigate(`/families/${activeFamilyId}/search`)}
                className="rounded-xl p-2.5 text-heritage-500 transition hover:bg-heritage-100 md:hidden"
                aria-label={t('Поиск по семье')}
              >
                <Icon name="search" />
              </button>
            )}
            <NavLink
              to="/notifications"
              className={({ isActive }) =>
                `rounded-xl p-2.5 transition hover:bg-heritage-100 ${
                  isActive ? 'text-heritage-700' : 'text-heritage-500'
                }`
              }
              aria-label={t('Уведомления')}
            >
              <Icon name="bell" />
            </NavLink>
            <NavLink
              to="/account"
              className={({ isActive }) =>
                `rounded-xl p-2.5 transition hover:bg-heritage-100 ${
                  isActive ? 'text-heritage-700' : 'text-heritage-500'
                }`
              }
              aria-label={t('Профиль')}
            >
              <Icon name="user" />
            </NavLink>
          </div>
        </div>
        {family && (
          <div className="mx-auto hidden max-w-6xl gap-1 px-4 pb-2 md:flex">
            {items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium transition ${
                    isActive
                      ? 'bg-heritage-600 text-white'
                      : 'text-heritage-600 hover:bg-heritage-100'
                  }`
                }
              >
                <Icon name={item.icon} className="h-4 w-4" />
                {t(item.label)}
                {item.badge ? (
                  <span
                    className="ml-0.5 rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white"
                    title={t('Непрочитанных сообщений: {count}', {
                      count: item.badge,
                    })}
                  >
                    {item.badge > 99 ? '99+' : item.badge}
                  </span>
                ) : null}
              </NavLink>
            ))}
          </div>
        )}
      </header>

      <main className="mx-auto w-full max-w-6xl space-y-5 overflow-x-clip px-4 pb-24 pt-5 md:pb-10">
        <AppUpdateBanner />
        <PushEnablePrompt />
        <Outlet />
      </main>

      {canUseAssistant && activeFamilyId && (
        <>
          <button
            type="button"
            onClick={() => setAssistantOpen(true)}
            className="fixed bottom-[calc(5rem+env(safe-area-inset-bottom))] right-4 z-[1100] flex h-14 w-14 items-center justify-center rounded-full bg-heritage-600 text-white shadow-lg transition hover:bg-heritage-700 md:bottom-6"
            aria-label={t('Открыть ИИ-ассистента')}
            title={t('ИИ-ассистент')}
          >
            <Icon name="sparkles" className="h-6 w-6" />
          </button>
          {assistantOpen && (
            <AssistantPanel
              familyId={activeFamilyId}
              initialFiles={assistantInitialFiles ?? undefined}
              onInitialFilesConsumed={() => setAssistantInitialFiles(null)}
              onClose={() => setAssistantOpen(false)}
            />
          )}
        </>
      )}

      <nav className="fixed inset-x-0 bottom-0 z-[1000] border-t border-heritage-100 bg-surface/95 pb-[env(safe-area-inset-bottom)] backdrop-blur md:hidden">
        <div className="mx-auto flex max-w-md items-stretch justify-between px-2">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `flex min-w-0 flex-1 flex-col items-center gap-1 px-1 py-2.5 text-[10px] font-medium transition ${
                  isActive ? 'text-heritage-700' : 'text-heritage-400'
                }`
              }
            >
              <span className="relative">
                <Icon name={item.icon} className="h-5 w-5" />
                {item.badge ? (
                  <span
                    className="absolute -right-2.5 -top-2 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold leading-none text-white"
                    title={t('Непрочитанных сообщений: {count}', {
                      count: item.badge,
                    })}
                  >
                    {item.badge > 99 ? '99+' : item.badge}
                  </span>
                ) : null}
              </span>
              <span className="max-w-full truncate">{t(item.label)}</span>
            </NavLink>
          ))}
        </div>
      </nav>

    </div>
  );
}
